// Bileli · modules/media/viewmodel — live per-asset upload status for badges/placeholders. Byte phase
// wins over encode phase (mirrors frontend/web useMediaUploadStatus); undefined once READY / not tracked.

import { useStoreState } from '../../../shared/lib/zustandTeact';
import { encodeQueuedStore, finalizeProgressStore, resumingStore } from '../store/liveStores';

export type MediaStatus =
  | { phase: 'uploading'; pct: number }
  /** In Bunny's shared queue — NOT started. Carries no percentage, deliberately (see below). */
  | { phase: 'queued' }
  | { phase: 'processing'; pct?: number }
  | undefined;

/**
 * The phase decision, pure so the contract can be tested without stores.
 *
 * The order is the point:
 * 1. **Bytes win.** While the file is still uploading, nothing about encoding is interesting yet.
 * 2. **Queued beats a percentage** — including a stale one. A video that was transcoding and gets
 *    re-reported as queued must not keep showing its old number; and while queued the backend suppresses
 *    encodeProgress to null precisely because Bunny pins it to 0 for the entire wait. Rendering that 0
 *    was most of why a slow upload read as a broken one, so this ordering IS the fix, not a nicety.
 * 3. Otherwise a real percentage, or a bare "processing" when we have none.
 */
export function resolveMediaStatus(
  resumePct: number | undefined, encodePct: number | undefined, queued: boolean | undefined,
): MediaStatus {
  if (resumePct !== undefined) return { phase: 'uploading', pct: resumePct };
  if (queued) return { phase: 'queued' };
  if (encodePct !== undefined) return { phase: 'processing', pct: encodePct };
  return undefined;
}

export function useMediaStatus(mediaId?: string): MediaStatus {
  const resumePct = useStoreState(resumingStore, (m) => (mediaId ? m[mediaId] : undefined));
  const encodePct = useStoreState(finalizeProgressStore, (m) => (mediaId ? m[mediaId] : undefined));
  const queued = useStoreState(encodeQueuedStore, (m) => (mediaId ? m[mediaId] : undefined));
  if (!mediaId) return undefined;
  return resolveMediaStatus(resumePct, encodePct, queued);
}
